import { Component, inject, computed, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { formatNumber, TitleCasePipe } from '@angular/common';

import { OrderFlowAuthService } from '../auth/order-flow-auth.service';
import { PortfoliosService } from '../counterparty-data/portfolios/portfolios.service';
import { SecuritiesService } from '../counterparty-data/securities/securities.service';
import { ToastComponent } from '../shared/toast.component';
import { SpinnerComponent } from '../shared/spinner.component';
import { ROLE_ORDER_WRITER } from '../shared/types/constants';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'of-refresh-cache-menu',
  imports: [ToastComponent, SpinnerComponent, TitleCasePipe],
  template: `
    @if (canRefresh()) {
      <li class="nav-item dropdown">
        <button
          class="of-tbar__icon-btn of-rc__btn dropdown-toggle"
          type="button"
          data-bs-toggle="dropdown"
          data-bs-auto-close="true"
          aria-expanded="false"
          title="Refresh cache"
        >
          <i class="bi bi-arrow-clockwise"></i>
        </button>
        <ul class="dropdown-menu dropdown-menu-end of-tbar__dropdown of-rc__menu">
          <li>
            <button class="dropdown-item of-rc__item" type="button" (click)="refreshPortfolios()">
              <i class="bi bi-briefcase"></i>
              <span>Refresh Portfolios</span>
            </button>
          </li>
          <li>
            <button class="dropdown-item of-rc__item" type="button" (click)="refreshSecurities()">
              <i class="bi bi-graph-up"></i>
              <span>Refresh Securities</span>
            </button>
          </li>
        </ul>
      </li>
    }

    <of-toast #toastProgress [showCloseButton]="false">
      <div id="title" class="d-flex align-items-center gap-2">
        <of-spinner [diameter]="14"></of-spinner>
        <span>Refreshing {{ refreshHeader() | titlecase }}</span>
      </div>
      <div id="content" class="of-toast-notify__body">Fetching latest data from server</div>
    </of-toast>

    <of-toast #toastSuccess [timeout]="4000">
      <div id="title" class="d-flex align-items-center gap-2">
        <i class="bi bi-check-circle-fill of-toast-notify__success-icon"></i>
        <span>{{ refreshHeader() | titlecase }} Refreshed</span>
      </div>
      <div id="content" class="of-toast-notify__body">{{ refreshBody() }}</div>
    </of-toast>
  `,
  styles: [
    `
      .of-rc__btn::after {
        display: none;
      }

      .of-rc__menu {
        min-width: 190px;
      }

      .of-rc__item {
        align-items: center;
        display: flex;
        gap: 0.55rem;
      }
    `
  ]
})
export class RefreshCacheMenuComponent {
  private readonly authService = inject(OrderFlowAuthService);
  private readonly portfoliosService = inject(PortfoliosService);
  private readonly securitiesService = inject(SecuritiesService);

  private readonly loggedIn = toSignal(this.authService.loggedIn$, { initialValue: false });
  protected readonly canRefresh = computed(() => this.loggedIn() && this.authService.hasRole(ROLE_ORDER_WRITER));

  protected readonly refreshHeader = signal('');
  protected readonly refreshBody = signal('');

  protected readonly toastProgress = viewChild<ToastComponent>('toastProgress');
  protected readonly toastSuccess = viewChild<ToastComponent>('toastSuccess');

  protected refreshPortfolios(): void {
    this.refreshHeader.set('portfolios');
    this.toastProgress()?.show();
    this.portfoliosService.refreshPortfoliosCache$().subscribe({
      next: (portfolios) => {
        this.refreshBody.set(`${formatNumber(portfolios.length, 'en-US')} portfolios loaded`);
        this.toastProgress()?.hide();
        this.toastSuccess()?.show();
      },
      error: () => this.toastProgress()?.hide()
    });
  }

  protected refreshSecurities(): void {
    this.refreshHeader.set('securities');
    this.toastProgress()?.show();
    this.securitiesService.refreshSecuritiesCache$().subscribe({
      next: (securities) => {
        this.refreshBody.set(`${formatNumber(securities.length, 'en-US')} securities loaded`);
        this.toastProgress()?.hide();
        this.toastSuccess()?.show();
      },
      error: () => this.toastProgress()?.hide()
    });
  }
}
